import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Form, Field } from '@availity/form';
import '@availity/yup';
import * as yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faXmark,
    faChevronRight,
    faChevronDown,
    faCheck,
} from '@fortawesome/free-solid-svg-icons';
import { ProjectVariableOption } from '../../../../../../models/DataRequestHub/ProjectVariable';
import Helpers from '../../../../../../utils/helper';
import ConfirmationModal from '../../../../../../components/Modal/ConfirmationModal';
import './FolderConfigurationVariableForm.scss';

const ROOT_FOLDER_PATH = '__ROOT__';

interface FolderNode {
    name: string;
    fullPath: string;
    children: Map<string, FolderNode>;
    option?: ProjectVariableOption;
}

export interface FolderConfigurationVariableFormProps {
    options: Array<ProjectVariableOption>;
    setOptions: (options: Array<ProjectVariableOption>) => void;
}

export const FolderConfigurationVariableForm = (
    props: FolderConfigurationVariableFormProps
) => {
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
        new Set()
    );
    const [addingToPath, setAddingToPath] = useState<string>('');
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [foldersToDelete, setFoldersToDelete] = useState<string[]>([]);
    const hasInitializedExpanded = useRef(false);

    const folderNameValidationSchema = yup.object().shape({
        folderName: yup
            .string()
            .trim()
            .required('Folder name is required')
            .test(
                'no-folder-separators',
                'Folder name cannot contain folder separator characters (/ or \\)',
                (value) => !value || (!value.includes('/') && !value.includes('\\'))
            )
            .test(
                'no-illegal-chars',
                `Folder name cannot contain prohibited characters`,
                (value) => !Helpers.hasFoldersPathError(value)
            ),
    });

    const { folderTree, allFolderPaths } = useMemo(() => {
        const root = new Map<string, FolderNode>();
        const paths = new Set<string>();

        props.options.forEach((option) => {
            const path = option.option.trim();
            const parts = path.split('/').filter((p) => p.trim());

            let currentLevel = root;
            let currentPath = '';

            parts.forEach((part, index) => {
                currentPath += (currentPath ? '/' : '') + part;

                if (index < parts.length - 1 || parts.length > 1) {
                    paths.add(currentPath);
                }

                if (!currentLevel.has(part)) {
                    currentLevel.set(part, {
                        name: part,
                        fullPath: currentPath,
                        children: new Map<string, FolderNode>(),
                        option: index === parts.length - 1 ? option : undefined,
                    });
                }

                const node = currentLevel.get(part)!;
                if (index === parts.length - 1) {
                    node.option = option;
                }
                currentLevel = node.children;
            });
        });

        return { folderTree: root, allFolderPaths: paths };
    }, [props.options]);

    useEffect(() => {
        if (!hasInitializedExpanded.current && allFolderPaths.size > 0) {
            setExpandedFolders(new Set(allFolderPaths));
            hasInitializedExpanded.current = true;
        }
    }, [allFolderPaths]);

    const toggleFolder = (path: string) => {
        const newExpanded = new Set(expandedFolders);
        if (newExpanded.has(path)) {
            newExpanded.delete(path);
        } else {
            newExpanded.add(path);
        }
        setExpandedFolders(newExpanded);
    };

    const getAllNestedFolderPaths = (folderPath: string): string[] => {
        const nestedPaths: string[] = [];
        props.options.forEach((option) => {
            const optionPath = option.option.trim();
            if (
                optionPath === folderPath ||
                optionPath.startsWith(folderPath + '/')
            ) {
                nestedPaths.push(optionPath);
            }
        });
        return nestedPaths.sort();
    };

    const onDeleteFolderClick = (option: ProjectVariableOption) => {
        const folderPath = option.option.trim();
        const allPathsToDelete = getAllNestedFolderPaths(folderPath);
        setFoldersToDelete(allPathsToDelete);
        setShowDeleteModal(true);
    };

    const confirmDeleteFolder = () => {
        const newOptions = props.options.filter(
            (opt) => !foldersToDelete.includes(opt.option.trim())
        );
        props.setOptions(newOptions);
        setShowDeleteModal(false);
        setFoldersToDelete([]);
    };

    const renderDeleteConfirmationText = () => {
        return (
            <div className="confirmation-body-text">
                <div>
                    Are you sure you want to delete the following folder(s)?
                </div>
                <ul>
                    {foldersToDelete.map((path) => (
                        <li key={path}>{path}</li>
                    ))}
                </ul>
            </div>
        );
    };

    const addSubFolder = (folderName: string, parentPath: string) => {
        const newPath = parentPath
            ? `${parentPath}/${folderName.trim()}`
            : folderName.trim();
        const newOption: ProjectVariableOption = {
            id: 0,
            name: '',
            option: newPath,
        };

        props.setOptions([...props.options, newOption]);
        setAddingToPath('');

        const newExpanded = new Set(expandedFolders);
        if (parentPath) {
            newExpanded.add(parentPath);
        }
        newExpanded.add(newPath);
        setExpandedFolders(newExpanded);
    };

    const renderAddFolderForm = (parentPath: string) => {
        return (
            <Form
                initialValues={{ folderName: '' }}
                validationSchema={folderNameValidationSchema}
                onSubmit={(values: { folderName: string }) => {
                    addSubFolder(values.folderName, parentPath);
                }}>
                {({ isValid, handleSubmit }: any) => (
                    <div className="add-folder-form">
                        <span className="folder-chevron-spacer"></span>
                        <Field
                            name="folderName"
                            autoFocus
                            onKeyPress={(e: any) => {
                                if (e.key === 'Enter' && isValid) {
                                    e.preventDefault();
                                    handleSubmit();
                                }
                            }}
                        />

                        <FontAwesomeIcon
                            className="btn-icon btn-icon-confirm"
                            onClick={handleSubmit}
                            icon={faCheck}
                        />
                        <FontAwesomeIcon
                            className="btn-icon btn-icon-cancel"
                            onClick={() => {
                                setAddingToPath('');
                            }}
                            icon={faXmark}
                        />
                    </div>
                )}
            </Form>
        );
    };

    const renderFolderTree = (
        nodes: Map<string, FolderNode>,
        level: number = 0
    ): JSX.Element[] => {
        const elements: JSX.Element[] = [];

        nodes.forEach((node) => {
            const isExpanded = expandedFolders.has(node.fullPath);
            const hasChildren = node.children.size > 0;
            const isAddingHere = addingToPath === node.fullPath;

            elements.push(
                <div key={node.fullPath}>
                    <div
                        className="folder-item"
                        style={{ paddingLeft: `${level * 20}px` }}>
                        <span className="folder-content">
                            {hasChildren ? (
                                <FontAwesomeIcon
                                    icon={
                                        isExpanded
                                            ? faChevronDown
                                            : faChevronRight
                                    }
                                    className="folder-chevron"
                                    onClick={() => toggleFolder(node.fullPath)}
                                />
                            ) : (
                                <span className="folder-chevron-spacer"></span>
                            )}
                            <span className="folder-name">{node.name}</span>
                        </span>
                        {node.option && (
                            <span
                                className="delete-button"
                                onClick={() =>
                                    onDeleteFolderClick(node.option!)
                                }>
                                <FontAwesomeIcon icon={faXmark} />
                            </span>
                        )}
                    </div>

                    {(isExpanded || !hasChildren) && (
                        <>
                            {isExpanded && hasChildren && (
                                <>
                                    {renderFolderTree(node.children, level + 1)}
                                </>
                            )}

                            <div
                                className="add-subfolder-wrapper"
                                style={{
                                    paddingLeft: `${(level + 1) * 20}px`,
                                }}>
                                {isAddingHere ? (
                                    renderAddFolderForm(node.fullPath)
                                ) : (
                                    <button
                                        className="btn-link add-subfolder-link"
                                        onClick={() => {
                                            setAddingToPath(node.fullPath);
                                        }}>
                                        <span className="folder-chevron-spacer"></span>
                                        Add Sub Folder
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            );
        });

        return elements;
    };

    return (
        <div className="folder-configuration-variable-form">
            <div className="folder-tree-container">
                {renderFolderTree(folderTree)}

                <div className="add-root-folder-wrapper">
                    {addingToPath === ROOT_FOLDER_PATH ? (
                        renderAddFolderForm('')
                    ) : (
                        <button
                            className="btn-link add-new-folder-link"
                            onClick={() => {
                                setAddingToPath(ROOT_FOLDER_PATH);
                            }}>
                            Add new folder
                        </button>
                    )}
                </div>
            </div>

            <ConfirmationModal
                isVisible={showDeleteModal}
                setIsVisible={setShowDeleteModal}
                header="Delete Folder"
                confirmationText={renderDeleteConfirmationText()}
                onConfirm={confirmDeleteFolder}
                nextButtonText="Delete"
                cancelButtonText="Cancel"
                isConfirmationDanger={true}
            />
        </div>
    );
};
